import { StyledTable, StyledTableRow } from '@/shared/ui/tables'
import React, { forwardRef, JSX, ReactNode, MouseEvent } from 'react'
import {
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableFooter,
    Paper,
    SxProps,
    Theme,
} from '@mui/material'

interface Column<T> {
    id: string
    label: string
    className?: string
    isAction?: boolean
    align?: 'left' | 'center' | 'right'
    width?: string | number
    render?: (row: T, index: number) => ReactNode
}

interface DataTableProps<T> {
    columns: Column<T>[]
    data: T[]
    footer?: ReactNode
    striped?: boolean
    stickyHeader?: boolean
    maxHeight?: string | number
    sx?: SxProps<Theme>
    onRowClick?: (
        event: MouseEvent<HTMLTableRowElement>,
        row: T,
        index: number
    ) => void
}

function DataTableInner<T extends Record<string, unknown>>(
    {
        columns,
        data,
        footer,
        striped = true,
        stickyHeader = false,
        maxHeight,
        sx,
        onRowClick,
    }: DataTableProps<T>,
    ref: React.Ref<HTMLDivElement>
) {
    const hasHoverActions = columns.some((column) => column.isAction)

    return (
        <TableContainer
            ref={ref}
            component={Paper}
            sx={{
                maxHeight,
                ...sx,
            }}
        >
            <StyledTable stickyHeader={stickyHeader} size="small" striped>
                <TableHead>
                    <TableRow
                        sx={{
                            '& .MuiTableCell-head, th': {
                                textAlign: 'left',
                                fontWeight: 'bold',
                            },
                        }}
                    >
                        {columns.map((column) => (
                            <TableCell
                                key={column.id}
                                align={column.align ?? 'left'}
                                sx={{
                                    width: column.width,
                                    fontWeight: 600,
                                }}
                            >
                                {column.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody sx={{}}>
                    {data.map((row, rowIndex) => (
                        <StyledTableRow
                            key={rowIndex}
                            hover
                            hoverActions={hasHoverActions}
                            verticalAlign="top"
                            className={
                                hasHoverActions ? 'contentRow' : undefined
                            }
                            onClick={(event) =>
                                onRowClick?.(event, row, rowIndex)
                            }
                            sx={{
                                cursor: onRowClick ? 'pointer' : 'default',
                                bgcolor:
                                    striped && rowIndex % 2 === 0
                                        ? 'action.hover'
                                        : undefined,
                            }}
                        >
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    className={column.className}
                                    data-cell-inline-edit={
                                        column.isAction ? true : undefined
                                    }
                                    align={column.align ?? 'left'}
                                >
                                    {column.render
                                        ? column.render(row, rowIndex)
                                        : String(row[column.id] ?? '')}
                                </TableCell>
                            ))}
                        </StyledTableRow>
                    ))}
                </TableBody>
                {footer && <TableFooter>{footer}</TableFooter>}
            </StyledTable>
        </TableContainer>
    )
}

export const DataTable = forwardRef(DataTableInner) as <
    T extends Record<string, unknown>
>(
    props: DataTableProps<T> & { ref?: React.Ref<HTMLDivElement> }
) => JSX.Element
